import { ReactNode } from "react";
import { useAppSelector } from "app/hooks";
import FeatureToggle from "enums/FeatureToggle";
import { selectFeatureToggle } from "./featureToggleSlice";

/**
 * FeatureGate component conditionally renders children based on a feature toggle.
 *
 * @param flag - The feature toggle to check
 * @param showWhenEnabled - If true, show children when the toggle is enabled (ON).
 *                          If false, show children when the toggle is disabled (OFF).
 *                          Defaults to true.
 * @param children - The content to render when the condition is met
 *
 * @example
 * // Render children only when USE_OUTER_LIMITS is disabled (OFF)
 * <FeatureGate flag={FeatureToggle.USE_OUTER_LIMITS} showWhenEnabled={false}>
 *   <PreviewTag />
 * </FeatureGate>
 *
 * @example
 * // Render children only when AI_SCORING is enabled (ON)
 * <FeatureGate flag={FeatureToggle.AI_SCORING}>
 *   <AiScoringSection />
 * </FeatureGate>
 */
export function FeatureGate({
    flag,
    showWhenEnabled = true,
    children,
}: {
    flag: FeatureToggle;
    /** If true, show when toggle is ON. If false, show when toggle is OFF. Defaults to true. */
    showWhenEnabled?: boolean;
    children: ReactNode;
}) {
    const isOn = useAppSelector((state) => selectFeatureToggle(state, flag));
    return isOn === showWhenEnabled ? <>{children}</> : null;
}
